import React from 'react';
import { CompetitorItem, getScoreBadgeClasses } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface CompetitorSummaryCardProps {
  items: CompetitorItem[];
  overallSummary?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const CompetitorSummaryCard: React.FC<CompetitorSummaryCardProps> = ({
  items,
  overallSummary,
  onViewDetails,
  onViewInTimeline,
}) => {
  const count = items?.length ?? 0;
  const timelineTs = items[0]?.timestamp_iso;

  const avgScore =
    count > 0
      ? Math.round(items.reduce((sum, item) => sum + (item.handling_score ?? 0), 0) / count)
      : null;

  // Group by competitor name
  const byCompetitor = items.reduce<Record<string, { count: number; avgScore: number }>>((acc, item) => {
    const name = item.competitor ?? 'Unknown';
    if (!acc[name]) acc[name] = { count: 0, avgScore: 0 };
    acc[name].count += 1;
    acc[name].avgScore += item.handling_score ?? 0;
    return acc;
  }, {});
  Object.keys(byCompetitor).forEach((name) => {
    byCompetitor[name].avgScore = Math.round(byCompetitor[name].avgScore / byCompetitor[name].count);
  });

  const badgeColor = avgScore == null ? 'bg-gray-100 text-gray-500 border border-gray-200' : getScoreBadgeClasses(avgScore);

  const statusBadge =
    count === 0 ? (
      <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">None</span>
    ) : avgScore != null ? (
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>Avg {avgScore}%</span>
    ) : undefined;

  const missedTalkingPoints: string[] = items
    .filter((item) => item.what_missed)
    .slice(0, 2)
    .map((item) => `${item.competitor}: ${item.what_missed}`);

  const resultContent = (
    <div className="space-y-2">
      {count === 0 ? (
        <p className="text-xs text-gray-500">No competitor mentions were detected in this call.</p>
      ) : (
        <>
          {overallSummary && <p className="text-xs text-gray-600">{overallSummary}</p>}
          <div className="flex items-center gap-3 mt-1">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-700">{count}</p>
              <p className="text-[10px] text-gray-400">mention{count !== 1 ? 's' : ''}</p>
            </div>
            {avgScore != null && (
              <div className="text-center">
                <p className={`text-xl font-bold ${avgScore >= 70 ? 'text-green-600' : avgScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {avgScore}%
                </p>
                <p className="text-[10px] text-gray-400">avg handling</p>
              </div>
            )}
          </div>
          {Object.keys(byCompetitor).length > 0 && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-400 uppercase">By Competitor</p>
              {Object.entries(byCompetitor).map(([name, data]) => (
                <div key={name} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                  <span className="text-xs text-gray-700 font-medium">{name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">{data.count}x</span>
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${getScoreBadgeClasses(data.avgScore)}`}>
                      {data.avgScore}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Competitor Handling"
      icon={<span>&#9876;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={missedTalkingPoints.length > 0 ? missedTalkingPoints : undefined}
      sectionKey="competitor"
      onViewDetails={onViewDetails}
      onViewInTimeline={timelineTs ? onViewInTimeline : undefined}
    />
  );
};

export default CompetitorSummaryCard;
